import { z } from "zod";
import type { FolhaDoMesInput } from "./folha";

export const VINCULOS = ["CLT", "PRO_LABORE", "PJ"] as const;
export type VinculoDoColaborador = (typeof VINCULOS)[number];
export const VINCULO_LABELS: Record<VinculoDoColaborador, string> = { CLT: "CLT", PRO_LABORE: "Pró-labore (sócio)", PJ: "PJ / autônomo" };
/** Encargos sugeridos: FGTS de 8% no CLT; pró-labore e PJ sem encargo do laboratório (o contador ajusta). */
export const ENCARGOS_PADRAO_BPS: Record<VinculoDoColaborador, number> = { CLT: 800, PRO_LABORE: 0, PJ: 0 };

export const SETORES = ["PRODUCAO", "ADMINISTRATIVO"] as const;
export type SetorDoColaborador = (typeof SETORES)[number];
export const SETOR_LABELS: Record<SetorDoColaborador, string> = { PRODUCAO: "Produção", ADMINISTRATIVO: "Administrativo" };

const centavos = z.number().int().min(0).max(100_000_000);
/** Custo além do salário e dos benefícios: comissão, uniforme, curso, exame. */
export const outroCustoSchema = z.object({ descricao: z.string().trim().min(1).max(60), valorCents: centavos });
export type OutroCusto = z.infer<typeof outroCustoSchema>;
export const SUGESTOES_DE_OUTROS_CUSTOS = ["Comissão", "Uniforme e EPI", "Cursos e treinamento", "Exames ocupacionais", "Ajuda de custo"] as const;
/** O que veio do banco (Json): o que não tiver o formato vira lista vazia. */
export const lerOutrosCustos = (v: unknown): OutroCusto[] => z.array(outroCustoSchema).catch([]).parse(v);

/** Comissão: % da receita que a pessoa gerou, por peça (tabela do laboratório) ou um valor fixo por mês. */
export const MODOS_DE_COMISSAO = ["NENHUMA", "PERCENTUAL", "POR_PECA", "FIXA"] as const;
export type ModoDeComissao = (typeof MODOS_DE_COMISSAO)[number];
export const MODO_DE_COMISSAO_LABELS: Record<ModoDeComissao, string> = { NENHUMA: "Nenhuma", PERCENTUAL: "% da receita", POR_PECA: "Por peça", FIXA: "Fixa" };

export const custoDoColaboradorSchema = z.object({
  userId: z.string().min(1),
  vinculo: z.enum(VINCULOS),
  setor: z.enum(SETORES),
  salarioCents: centavos,
  beneficiosCents: centavos,
  encargosBps: z.number().int().min(0).max(10_000),
  outrosCustos: z.array(outroCustoSchema).max(20).default([]),
  comissaoModo: z.enum(MODOS_DE_COMISSAO).default("NENHUMA"),
  comissaoBps: z.number().int().min(0).max(10_000).default(0),
  comissaoFixaCents: centavos.default(0),
  ativo: z.boolean(),
});
export type CustoDoColaboradorInput = z.infer<typeof custoDoColaboradorSchema>;

/** Tabela de comissão por peça do laboratório: valor por serviço e o padrão para os que não estão nela. */
export const tabelaDeComissaoSchema = z.object({
  padraoCents: centavos.nullable(),
  valores: z.array(z.object({ serviceCategoryId: z.string().min(1), valorCents: centavos })).max(1000),
});
export type TabelaDeComissao = { porServico: Record<string, number>; padraoCents: number };
/** Um serviço entregue no mês em que a pessoa é a responsável. */
export type EntregaDoColaborador = { serviceCategoryId: string | null; quantidade: number; valorCents: number | null };

/**
 * A comissão do mês. Percentual: sobre a receita das entregas (serviço sem preço não gera). Por
 * peça: unidades × valor do serviço na tabela, ou o padrão. Fixa: o valor, todo mês.
 */
export function comissaoDoMes(
  c: { comissaoModo?: ModoDeComissao; comissaoBps?: number; comissaoFixaCents?: number },
  entregas: EntregaDoColaborador[],
  tabela: TabelaDeComissao,
): number {
  switch (c.comissaoModo ?? "NENHUMA") {
    case "PERCENTUAL":
      return Math.round((entregas.reduce((t, e) => t + (e.valorCents ?? 0), 0) * (c.comissaoBps ?? 0)) / 10_000);
    case "POR_PECA":
      return entregas.reduce((t, e) => t + e.quantidade * ((e.serviceCategoryId ? tabela.porServico[e.serviceCategoryId] : undefined) ?? tabela.padraoCents), 0);
    case "FIXA":
      return c.comissaoFixaCents ?? 0;
    default:
      return 0;
  }
}

/** `comissaoCents` é a do mês, já calculada (comissaoDoMes): entra na remuneração e paga encargo. */
type Custo = Pick<CustoDoColaboradorInput, "vinculo" | "setor" | "salarioCents" | "beneficiosCents" | "encargosBps"> & { outrosCustos?: OutroCusto[]; comissaoCents?: number };

export const remuneracaoDe = (c: Custo) => c.salarioCents + (c.comissaoCents ?? 0);
export const encargosDe = (c: Custo) => Math.round((remuneracaoDe(c) * c.encargosBps) / 10_000);
export const outrosDe = (c: Custo) => (c.outrosCustos ?? []).reduce((t, o) => t + o.valorCents, 0);
/** O que o colaborador custa no mês: salário + comissão + encargos + benefícios + outros custos. */
export const custoMensal = (c: Custo) => remuneracaoDe(c) + c.beneficiosCents + encargosDe(c) + outrosDe(c);

/** A folha do mês montada a partir da equipe (o INSS da guia o contador informa). */
export function folhaDaEquipe(cs: (Custo & { ativo: boolean })[]): Pick<FolhaDoMesInput, "salariosProducaoCents" | "salariosAdministrativoCents" | "proLaboreCents" | "fgtsCents" | "beneficiosCents"> {
  const ativos = cs.filter((c) => c.ativo);
  const soma = (f: (c: Custo) => number, filtro: (c: Custo) => boolean) => ativos.filter(filtro).reduce((t, c) => t + f(c), 0);
  const assalariado = (c: Custo) => c.vinculo !== "PRO_LABORE";
  return {
    // A comissão do mês entra junto do salário (e paga o FGTS dela, no CLT).
    salariosProducaoCents: soma(remuneracaoDe, (c) => assalariado(c) && c.setor === "PRODUCAO"),
    salariosAdministrativoCents: soma(remuneracaoDe, (c) => assalariado(c) && c.setor === "ADMINISTRATIVO"),
    proLaboreCents: soma(remuneracaoDe, (c) => c.vinculo === "PRO_LABORE"),
    fgtsCents: soma(encargosDe, (c) => c.vinculo === "CLT"),
    // A folha tem uma linha só para o que não é salário nem encargo: benefícios e outros custos.
    beneficiosCents: soma((c) => c.beneficiosCents + outrosDe(c), () => true),
  };
}
